import { supabase } from '@/lib/supabase/client';
import type { DisplayType, DisplayTypeInsert, DisplayTypeUpdate } from '@/types/display-types';

export const displayTypesService = {
  async getAll(): Promise<DisplayType[]> {
    try {
      const { data, error } = await supabase.from('display_types').select('*').order('name', { ascending: true });

      if (error) {
        console.error('Error fetching display types:', error);
        return [];
      }

      return (data as DisplayType[]) || [];
    } catch (error) {
      console.error('Error fetching display types:', error);
      return [];
    }
  },

  async getById(id: string): Promise<DisplayType | null> {
    try {
      const { data, error } = await supabase.from('display_types').select('*').eq('id', id).single();

      if (error) {
        if (error.code === 'PGRST116') {
          // No rows returned
          return null;
        }
        console.error('Error fetching display type:', error);
        return null;
      }

      return (data as DisplayType) || null;
    } catch (error) {
      console.error('Error fetching display type:', error);
      return null;
    }
  },

  async create(data: DisplayTypeInsert): Promise<DisplayType> {
    try {
      const insertData = {
        name: data.name,
        description: data.description ?? null,
        size: data.size ?? null,
        category: data.category,
        selling_price: data.selling_price ?? null,
      };

      const { data: newDisplayType, error } = await supabase.from('display_types').insert(insertData).select().single();

      if (error) {
        console.error('Error creating display type:', error);
        throw new Error(`Failed to create display type: ${error.message}`);
      }

      return newDisplayType as DisplayType;
    } catch (error) {
      console.error('Error creating display type:', error);
      throw error;
    }
  },

  async update(id: string, data: DisplayTypeUpdate): Promise<DisplayType> {
    try {
      const updateData: Record<string, unknown> = {};

      // Only include fields that are provided
      if (data.name !== undefined) updateData.name = data.name;
      if (data.description !== undefined) updateData.description = data.description;
      if (data.size !== undefined) updateData.size = data.size;
      if (data.category !== undefined) updateData.category = data.category;
      if (data.selling_price !== undefined) updateData.selling_price = data.selling_price;

      const { data: updatedDisplayType, error } = await supabase
        .from('display_types')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error updating display type:', error);
        throw new Error(`Failed to update display type: ${error.message}`);
      }

      if (!updatedDisplayType) {
        throw new Error('Display type not found');
      }

      return updatedDisplayType as DisplayType;
    } catch (error) {
      console.error('Error updating display type:', error);
      throw error;
    }
  },

  async delete(id: string): Promise<void> {
    try {
      const { error } = await supabase.from('display_types').delete().eq('id', id);

      if (error) {
        console.error('Error deleting display type:', error);
        throw new Error(`Failed to delete display type: ${error.message}`);
      }
    } catch (error) {
      console.error('Error deleting display type:', error);
      throw error;
    }
  },
};

